<template>
  <form novalidate @submit.prevent="submit">
    <c-box font-size="md">
      <c-grid
        :d="{ md: 'grid' }"
        :template-columns="proFormaRequired ? '1fr 2fr 2fr 3fr 1fr 2fr' : '1fr 3fr 4fr 1fr 2fr'"
        align-items="center"
        :gap="{ md: 'md' }"
        pt="lg"
      >
        <c-form-control mb="0">
          <c-flex
            :mb="{ base: 'xs', md: '0' }"
            v-chakra="{
              'input[type=checkbox]+div': {
                borderRadius: 'var(--chakra-radii-input)',
                width: 'var(--chakra-sizes-6)',
                height: 'var(--chakra-sizes-6)',
              },
              'input[type=checkbox]:checked+div, input[type=checkbox][aria-checked=mixed]+div': {
                bg: 'brand.purple',
                borderColor: 'brand.purple',
              },
            }"
          >
            <c-form-label
              :for="'productrow-checkbox_' + index"
              :d="{ md: 'none' }"
              font-weight="bold"
            >
              {{ $t("common.selected") }}
            </c-form-label>
            <c-checkbox
              :id="'productrow-checkbox_' + index"
              :ml="{ base: 'auto', md: '0' }"
              :data-cy="'productrow-checkbox_' + index"
              :is-checked="isSelected"
              @change="handleSelect"
            />
          </c-flex>
        </c-form-control>

        <c-flex :mb="{ base: 'xs', md: '0' }">
          <c-text as="span" :d="{ md: 'none' }" font-weight="bold">
            {{ $t("productReturn.salesReturn.productSKUs") }}
          </c-text>
          <c-text
            as="span"
            :ml="{ base: 'auto', md: '0' }"
            :text-align="{ base: 'right', md: 'left' }"
          >
            {{ form.id }}
          </c-text>
        </c-flex>
        <c-flex :mb="{ base: 'xs', md: '0' }">
          <c-text as="span" :d="{ md: 'none' }" font-weight="bold">
            {{ $t("productReturn.salesReturn.productName") }}
          </c-text>
          <c-text
            as="span"
            :ml="{ base: 'auto', md: '0' }"
            :text-align="{ base: 'right', md: 'left' }"
          >
            {{ form.name }}
          </c-text>
        </c-flex>
        <c-box v-if="proFormaRequired" :mb="{ base: 'xs', md: '0' }">
          <c-form-control is-required :is-invalid="inputIsInvalid('tariffCode')" mb="0">
            <c-form-label :for="'category__' + index" :d="{ md: 'none' }" font-weight="bold">
              {{ $t("productReturn.salesReturn.returnReason") }}
            </c-form-label>
            <c-select
              :is-disabled="!isSelected"
              :id="'category__' + index"
              v-model="form.tariffCode"
              @blur="onFieldBlur('tariffCode')"
              @change="onFieldChange('tariffCode')"
              size="lg"
              border-radius="input"
            >
              <option v-for="type in categoryTypes" :key="type.value" :value="type.value">
                {{ $t(type.label) }}
              </option>
            </c-select>
          </c-form-control>
        </c-box>

        <c-flex :mb="{ base: 'xs', md: '0' }">
          <c-text as="span" :d="{ md: 'none' }" font-weight="bold">
            {{ $t("productReturn.salesReturn.orderQuantity") }}
          </c-text>
          <c-text
            as="span"
            :ml="{ base: 'auto', md: '0' }"
            :text-align="{ base: 'right', md: 'left' }"
          >
            {{ form.orderCount }}
          </c-text>
        </c-flex>

        <c-form-control mb="0">
          <c-box>
            <c-form-label for="returnCount" :d="{ md: 'none' }" font-weight="bold">
              {{ $t("productReturn.salesReturn.returnQuantity") }}
            </c-form-label>
            <c-flex gap="md" align-items="center">
              <!-- ref doesn't work on c-input, so use plain input instead -->
              <input
                ref="countInput"
                :disabled="!isSelected"
                id="returnCount"
                v-model.number="form.returnCount"
                type="number"
                min="1"
                :max="form.orderCount"
                maxlength="4"
                @change="handleCountChange()"
                @keyup="onFieldChange('returnCount')"
                @blur="onFieldBlur('returnCount')"
                v-chakra
                border-radius="input"
                width="100%"
                height="12"
                px="4"
                font-size="1rem !important"
              />
              <c-text>
                {{ $t("common.pcs") }}
              </c-text>
            </c-flex>
          </c-box>
        </c-form-control>
      </c-grid>

      <c-box pb="lg">
        <c-box v-if="proFormaRequired">
          <c-box v-if="isErrorField('tariffCode')" color="brand.red" font-size="sm" my="xs">
            {{
              $t("common.error.fieldInvalid", {
                field: $t("productReturn.common.customsInformation.categoryTitle"),
              })
            }}
          </c-box>
        </c-box>
        <c-box
          v-if="isSelected && isErrorField('returnCount')"
          color="brand.red"
          font-size="sm"
          my="xs"
        >
          {{
            $t("common.error.fieldInvalid", {
              field: $t("productReturn.salesReturn.returnQuantity"),
            })
          }}
        </c-box>
      </c-box>
    </c-box>
  </form>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { FormValidationMixin } from "@/components/FormValidationMixin.js";
import { required, numeric, minValue, maxValue } from "vuelidate/lib/validators";
import Icon from "@/design-system/elements/Icon.vue";
import Button from "@/design-system/elements/Button.vue";
import { getCategoryTypes } from "./RMATranslationHelpers";
import {
  CBox,
  CCheckbox,
  CFlex,
  CFormControl,
  CFormLabel,
  CGrid,
  CInput,
  CSelect,
  CText,
} from "@chakra-ui/vue";
import { inputMixin } from "./RMAMixins";

const orderCountValidation = {
  required,
  numeric,
  minValue: minValue(1),
  maxValue: maxValue(10000),
};

const returnCountValidation = {
  required,
  numeric,
  minValue: minValue(0),
  maxValue: maxValue(10000),
};

export default defineComponent({
  name: "RMASRProductsEditor",
  mixins: [inputMixin, FormValidationMixin],
  components: {
    Icon,
    Button,
    CBox,
    CCheckbox,
    CSelect,
    CText,
    CGrid,
    CInput,
    CFlex,
    CFormLabel,
    CFormControl,
  },
  props: {
    index: {
      type: Number,
      required: true,
    },
    count: {
      type: Number,
      required: true,
    },
    proFormaRequired: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      form: {
        name: "",
        id: "",
        orderCount: 0,
        returnCount: 0,
      },
      categoryTypes: getCategoryTypes(),
      isSelected: false,
    };
  },
  validations() {
    const validations: any = {
      form: {
        name: { required },
        orderCount: orderCountValidation,
        returnCount: returnCountValidation,
      },
    };
    if (this.proFormaRequired) {
      validations.form.tariffCode = {
        requiredIfSelected: (value: string | undefined) =>
          this.isSelected ? value !== undefined : true,
      };
    }
    return validations;
  },
  methods: {
    removeProduct(index: number) {
      this.$emit("onRemove", index);
    },
    handleCountChange() {
      this.form.returnCount = this.limitNumber(this.form.returnCount, 1, this.form.orderCount, 1);
      this.$nextTick(() => {
        this.$emit("onCountChanged");
      });
    },
    handleSelect() {
      this.isSelected = !this.isSelected;
      if (this.isSelected === false) {
        this.form.returnCount = 0;
      } else {
        this.form.returnCount = this.form.orderCount;
        this.$nextTick(() => {
          // Disabled elements apparently don't get a ref, which is why need to wait for next tick before we can use a ref to focus the input.
          this.$refs.countInput.focus();
        });
      }
      this.$emit("onCountChanged");
    },
    limitNumber(t: any, min: number, max: number, d: number): number {
      if (t) {
        const s = `${t}`;
        const v: any = Number(s.replace(/,/g, "."));
        if (!isNaN(v)) {
          if (v < min) {
            return min;
          }
          if (v > max) {
            return max;
          }
          return v;
        }
      }
      return d;
    },
    submit(): void {
      // Do nothing
    },
  },
});
</script>
